import { type TData, rowIdToRelativeRow, rowStringToCode } from "@package/grid-utils";
import type { CellClassParams, CellStyle } from "ag-grid-community";

import {
  type TPageFormattingOptions,
  useActivePageId,
  usePageFormattingOptions,
  type TPageFormatting,
  type TProduct,
  type TConditionalFormattingRulesGrouped,
  sortRules,
  useGridSettings,
  useQueryConditionalFormattingRules,
} from "../../../data";
import { getRowId, getRowType, rowIdToPeriod } from "../../../data/formatting/helpers";
import { getBackgroundForStatus, getComputedColor } from "./helpers";
import { colParams, isListOnlyPermissions, parseLimitRef } from "../../utils";
import { useMemoCallback } from "../../../utils/useMemoCallback";
import { brightColor, NO_COLOR } from "../../../utils/color";
import type { TColumnHeaderProps } from "../components";
import { useThemeMode } from "../../../context/theme";
import { getNodePeriod } from "../periodHelpers";
import { parseNumber } from "../../numbers";
import { useEffect, useMemo, useRef } from "react";
import { useGrid } from "../stores";
import { useUserId } from "../../../context/auth/hooks";

export type ProcessCellStyleFn = (params: CellClassParams, product?: TProduct) => CellStyle | undefined;

// Accessing the rules by iterating over them is expensive, so we create a map of columnId-rowId as the key, where the value is an array of rules that apply to that cell, sorted by priority.
type RulesMap = Record<string, TConditionalFormattingRulesGrouped[string][0][]>;

export const useProcessCellStyle = (): ProcessCellStyleFn => {
  const pageId = useActivePageId();
  const formatting = usePageFormattingOptions(pageId);
  const rules = useQueryConditionalFormattingRules(pageId);
  const calculateTextColor = useCalculateTextColor();
  const [api] = useGrid();

  // We create the rules map on initial load and whenever the rules change
  const rulesMap = useMemo(() => {
    const map: RulesMap = {};
    rules.data?.forEach((r) => {
      const columnId = r.column_id;
      const rowId = getRowId(r.row_id);
      const key = `${columnId}-${rowId}`;
      if (!map[key]) map[key] = [];
      map[key].push(r);
      map[key] = sortRules(map[key]);
    });
    return map;
  }, [rules.data]);

  const paintedMap = useRef<RulesMap | null>(null);
  useEffect(() => {
    if (!api || api.isDestroyed()) return;

    const painted = paintedMap.current;
    if (painted === rulesMap) return;
    paintedMap.current = rulesMap;

    // First run after (re)mount: the cells render fresh with the current rules, so no forced repaint is needed.
    if (!painted) return;

    // The grid is not remounted on page navigation and its rows are reused across pages, so AG Grid caches the (stable) cellStyle result and won't re-run it on its own so we must force a repaint when the rule set changes. A single refresh is not enough: with a large rule set the rules query for the new page resolves after its columns and values have already rendered, so a refresh that lands while the grid is still settling the new page fails to repaint the visible cells.
    const refresh = () => {
      if (!api.isDestroyed()) api.refreshCells({ force: true });
    };
    refresh();

    let repainted = false;
    const onModelUpdated = () => {
      if (repainted) return;
      repainted = true;
      api.removeEventListener("modelUpdated", onModelUpdated);
      refresh();
    };
    api.addEventListener("modelUpdated", onModelUpdated);
    return () => api.removeEventListener("modelUpdated", onModelUpdated);
  }, [api, rulesMap]);

  const process: ProcessCellStyleFn = (params, product) => {
    const isNotMonthColumn = params.colDef.field !== "period";
    const isNotSpreadColumn = params.colDef.field !== "month-timespread";
    const isNotWeekColumn = params.colDef.field !== "week";
    const isNotPeriodCodeColumn = params.colDef.field !== "period-code";
    const isNotCountColumn = params.colDef.field !== "count";
    const isNotCyclesColumn = params.colDef.field !== "cycles";
    const isNotCyclesPeriodsColumn = params.colDef.field !== "cycles-periods";
    const isNotOsnHalfColumn = params.colDef.field !== "osn-half";
    const perms = product?.packageByPackage.permissions;
    const isListOnly = isListOnlyPermissions(perms);
    const noPermissions = !perms?.length;
    const headerParams = params.column?.getUserProvidedColDef()?.headerComponentParams as TColumnHeaderProps | null;
    const status = headerParams?.status || "listen";
    const sharedCell =
      params?.data?.rowType === "mth" && headerParams?.sharedCellOffsets?.includes(params.rowIndex?.toString());

    const statusBackgroundColor = sharedCell ? getBackgroundForStatus(status) : "inherit";

    const period = getNodePeriod(params.node);

    const highlight = getHighlight(formatting.data, params.column.getColId(), period, params.colDef.field !== "period");

    if (
      isNotMonthColumn &&
      isNotSpreadColumn &&
      isNotWeekColumn &&
      isNotPeriodCodeColumn &&
      isNotCountColumn &&
      isNotCyclesColumn &&
      isNotCyclesPeriodsColumn &&
      isNotOsnHalfColumn &&
      (isListOnly || noPermissions)
    ) {
      return {
        background: "var(--disabled-column)",
        color: "inherit",
        fontWeight: "normal",
      };
    }

    const getConditional = () => {
      if (!period) return null;
      // Month/aggregate rows also look up legacy REL-* rules. Adhoc rows must not:
      // `data.period` is only the from-leg, so a month rule there would paint the strip.
      const relativeNodeId =
        params.node.data.rowType !== "adhoc" && params.node.data.period
          ? rowIdToRelativeRow[params.node.data.period]
          : undefined;
      const columnParams = colParams(params.column);
      const cellRowIds = [...new Set([period, relativeNodeId].filter((rowId): rowId is string => !!rowId))];
      const cellRules = cellRowIds.flatMap((rowId) => rulesMap?.[`${columnParams?.gridId}-${getRowId(rowId)}`] ?? []);
      if (!cellRules || !cellRules.length) return null;

      return sortRules(cellRules).find((x) => {
        const rowType = getRowType(x.row_id);

        // Typed row ids (`Sep26:mth`, `FEB26:MAR26:adhoc`) must match this row.
        // A strip formula with no suffix still contains `:`, so only compare when we peeled a type.
        if (rowType && rowType !== params.node.data.rowType) return null;

        // If the row_id uses the old format (which was broken for adhoc rows), we don't apply the rule if the row is an adhoc one.
        if (!x.row_id.includes(":") && params.node.data.rowType === "adhoc") return null;

        const isCell = x.column_id === params.column.getColId() && rowIdToPeriod(x.row_id) === period;
        if (!isCell || typeof params.value !== "number") return null;
        const met = ruleMet(params, x, params.value);
        return met ? x : null;
      });
    };

    const conditional = getConditional();

    if (conditional) {
      const bg = conditional.bg_color || highlight?.color || "inherit";
      const textColor = calculateTextColor({
        value: params.value,
        bg: bg,
        invert: conditional.invert_text_color,
      });
      return {
        background: bg,
        color: textColor,
        fontWeight: conditional.bold_text ? "bold" : "normal",
      };
    }

    const hasHighlight = !!highlight?.color && highlight?.color !== "inherit" && highlight?.color !== "#ffffff00";

    const bg = hasHighlight ? (highlight?.color as string) : statusBackgroundColor;

    const textColor = calculateTextColor({
      value: params.value,
      bg,
      invert: !!highlight?.invertTextColor,
    });

    const color = bg.includes("var") && !getComputedColor(bg) ? "inherit" : textColor;

    return {
      color,
      background: bg,
      fontWeight: highlight?.boldText ? "bold" : "normal",
    };
  };

  return useMemoCallback(process);
};

function getHighlight(
  pageFormatting: TPageFormattingOptions | undefined,
  colId: string,
  period: string,
  includePeriodHighlight = true,
) {
  if (!pageFormatting || !period) return null;

  const { cell_highlights, column_highlights, period_highlights } = pageFormatting;

  const cellKey = `${colId}-${period}`;

  return mergeRules(
    includePeriodHighlight ? period_highlights[rowStringToCode(period)] : null,
    column_highlights[colId],
    cell_highlights[cellKey],
  );
}

function ruleMet(
  params: CellClassParams<TData>,
  rule: TConditionalFormattingRulesGrouped[string][0],
  cellValue: number,
  acc?: TConditionalFormattingRulesGrouped[string][0],
) {
  const limitIsRef = rule.limit.includes(":");

  const limitNumber = limitIsRef ? getCellValueByRef(params, rule.limit) : parseNumber(rule.limit);

  if (limitNumber !== 0 && !limitNumber) return acc || undefined;

  return conditionalRuleMet(rule.rule, cellValue, limitNumber);
}

function conditionalRuleMet(rule: string, source: number, target: number) {
  switch (rule) {
    case "equals":
      return source === target;
    case "isNotEqual":
      return source !== target;
    case "lessThan":
      return source < target;
    case "moreThan":
      return source > target;
    case "lessThanOrEqual":
      return source <= target;
    case "moreThanOrEqual":
      return source >= target;
  }
}

function getCellValueByRef(params: CellClassParams<TData>, ref: string) {
  const { columnId, rowId } = parseLimitRef(ref);
  if (!columnId || !rowId) return;

  const rowNode = params.api.getRowNode(rowId);
  if (!rowNode) return;

  const value = params.api.getCellValue({ rowNode, colKey: columnId });

  return value;
}

const mergeRules = (...rules: (TPageFormatting[keyof TPageFormatting] | null | undefined)[]) =>
  rules.reduce<TPageFormatting[keyof TPageFormatting] | null>((acc, b) => {
    if (!b) return acc;
    if (!acc) return b;
    return {
      color: b.color || acc.color,
      boldText: b.boldText || acc.boldText,
      invertTextColor: b.invertTextColor || acc.invertTextColor,
    };
  }, null);

const black = "var(--dark-mode-elevation-base)";
const white = "var(--muted-100)";
const useCalculateTextColor = () => {
  const settings = useGridSettings(useUserId());
  const darkMode = useThemeMode() === "dark";
  return (props: { bg: string; value: string | number; invert: boolean }) => {
    const hasBackground = props.bg !== NO_COLOR && props.bg !== "inherit" && props.bg !== "#ffffff00";

    // Apply red text color for negatives UNLESS the background is the red preset color
    if (
      settings.data?.display_negative_color &&
      typeof props.value === "number" &&
      props.value < 0 &&
      props.bg.toUpperCase() !== "#D62728"
    ) {
      return "red";
    }

    if (!hasBackground) {
      if (props.invert) return darkMode ? black : white;
      return darkMode ? white : black;
    }

    const background = getComputedColor(props.bg);
    const isBright = brightColor(background);
    if (props.invert && isBright) return white;
    if (props.invert && !isBright) return black;
    return isBright ? black : white;
  };
};
